import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Separator } from "./ui/separator";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadialBarChart, RadialBar, LineChart, Line
} from "recharts";
import { TrendingUp, TrendingDown, IndianRupee, Building2, LayoutGrid, PieChartIcon, Lightbulb, BarChart3 } from "lucide-react";
import { calculateOurOfferMetrics } from "../utils/calculations";

const COLORS = {
  primary: '#1e40af',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  pink: '#ec4899',
  slate: '#64748b',
  emerald: '#10b981',
  orange: '#f97316',
};

const PIE_COLORS = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.purple, COLORS.cyan];

const formatCrores = (value) => {
  if (!value || isNaN(value)) return '0';
  return (value / 10000000).toFixed(2);
};

const formatLakhs = (value) => {
  if (!value || isNaN(value)) return '0';
  return (value / 100000).toFixed(2);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-sm shadow-lg p-3">
        <p className="font-semibold text-sm">{label || payload[0].name}</p>
        <p className="text-primary text-sm">
          ₹{formatCrores(payload[0].value)} Cr
        </p>
      </div>
    );
  }
  return null;
};

const JVComparisonCard = ({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, plData }) => {
  const comparison = useMemo(() => {
    if (!plData || !fsiBreakdown) return null;

    // Compute both JV scenarios
    const areaFormData = { ...formData, jvType: "area_sharing" };
    const revenueFormData = { ...formData, jvType: "revenue_sharing" };

    const areaMetrics = calculateOurOfferMetrics(areaFormData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
    const revenueMetrics = calculateOurOfferMetrics(revenueFormData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);

    if (!areaMetrics || !revenueMetrics) return null;

    return { area: areaMetrics, revenue: revenueMetrics };
  }, [formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, plData]);

  if (!comparison) return null;

  const { area, revenue } = comparison;
  const areaBetter = area.grossProfit > revenue.grossProfit;
  const diff = Math.abs(area.grossProfit - revenue.grossProfit);
  const currentType = formData.jvType || "area_sharing";

  return (
    <Card className="rounded-sm md:col-span-2 border-blue-200 bg-blue-50/10" data-testid="jv-comparison-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-blue-600" />
          JV Structure Comparison
        </CardTitle>
        <CardDescription className="text-xs">Side-by-side P&L for Area Sharing vs Revenue Sharing at {formData.jvRatioPercent || 30}% landowner share</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* Area Sharing Column */}
          <div className={`p-4 rounded border-2 ${currentType === 'area_sharing' ? 'border-blue-500 bg-blue-50/30' : 'border-muted'}`}>
            <div className="flex justify-between items-center mb-3">
              <span className="font-semibold text-sm">Area Sharing</span>
              {currentType === 'area_sharing' && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">SELECTED</span>}
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Developer Sale Area</span><span className="font-mono">{Math.round(area.developerSaleAreaSqft).toLocaleString('en-IN')} sft</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Partner Area Given</span><span className="font-mono">{Math.round(area.jvPartnerAreaSqft || 0).toLocaleString('en-IN')} sft</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST on Partner Area</span><span className="font-mono text-amber-600">₹{formatCrores(area.jvGSTOnPartnerArea)} Cr</span></div>
              <Separator className="my-1" />
              <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span className="font-mono">₹{formatCrores(area.totalRevenue)} Cr</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Expenses</span><span className="font-mono">₹{formatCrores(area.totalExpenses)} Cr</span></div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Net Profit</span>
                <span className={area.grossProfit >= 0 ? 'text-green-700' : 'text-destructive'}>₹{formatCrores(area.grossProfit)} Cr</span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Margin</span><span className="font-mono">{area.profitMarginPercent?.toFixed(1)}%</span></div>
            </div>
          </div>

          {/* Revenue Sharing Column */}
          <div className={`p-4 rounded border-2 ${currentType === 'revenue_sharing' ? 'border-blue-500 bg-blue-50/30' : 'border-muted'}`}>
            <div className="flex justify-between items-center mb-3">
              <span className="font-semibold text-sm">Revenue Sharing</span>
              {currentType === 'revenue_sharing' && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">SELECTED</span>}
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Developer Sale Area</span><span className="font-mono">{Math.round(revenue.finalSaleAreaSqft).toLocaleString('en-IN')} sft</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Partner Revenue (RR)</span><span className="font-mono">{Math.round(revenue.jvPartnerRevenue || 0).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST on Partner Area</span><span className="font-mono text-muted-foreground">N/A</span></div>
              <Separator className="my-1" />
              <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span className="font-mono">₹{formatCrores(revenue.totalRevenue)} Cr</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Expenses</span><span className="font-mono">₹{formatCrores(revenue.totalExpenses)} Cr</span></div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Net Profit</span>
                <span className={revenue.grossProfit >= 0 ? 'text-green-700' : 'text-destructive'}>₹{formatCrores(revenue.grossProfit)} Cr</span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Margin</span><span className="font-mono">{revenue.profitMarginPercent?.toFixed(1)}%</span></div>
            </div>
          </div>
        </div>

        {/* Recommendation */}
        <div className={`p-3 rounded text-xs ${areaBetter ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
          <span className="font-semibold">{areaBetter ? 'Area Sharing' : 'Revenue Sharing'}</span> yields ₹{formatCrores(diff)} Cr higher profit.
          {currentType !== (areaBetter ? 'area_sharing' : 'revenue_sharing') && (
            <span className="text-amber-700 ml-1">Consider switching to the more profitable structure.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = ({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, plData }) => {
  
  // Calculate metrics
  const metrics = useMemo(() => {
    if (!plData) return null;
    
    const totalRevenue = plData.totalRevenue || 0;
    const totalExpenses = plData.totalExpenses || 0;
    const netProfit = plData.grossProfit || 0;
    const roi = plData.returnOnInvestment || 0;
    const profitMargin = plData.profitMarginPercent || 0;
    
    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      roi,
      profitMargin,
      isProfitable: netProfit >= 0
    };
  }, [plData]);

  // Cost breakdown data for pie chart
  const costBreakdownData = useMemo(() => {
    if (!plData) return [];
    
    return [
      { name: 'FSI & Sanction', value: fsiCosts?.totalCosts || 0, color: COLORS.primary },
      { name: 'DA Charges', value: daChargesCosts?.totalDACharges || 0, color: COLORS.warning },
      { name: plData?.isGreenfield ? 'JV/Project Costs' : 'Society Expenses', value: plData.totalSocietyExpenses || 0, color: COLORS.purple },
      { name: 'Construction', value: plData.totalConstructionCost || 0, color: COLORS.emerald },
      { name: 'Other Costs', value: plData.totalOtherCosts || 0, color: COLORS.cyan },
      { name: 'Bank Interest', value: plData.totalBankInterest || 0, color: COLORS.danger },
    ].filter(item => item.value > 0);
  }, [plData, fsiCosts, daChargesCosts]);

  // Revenue vs Expenses bar chart data
  const revenueExpenseData = useMemo(() => {
    if (!plData) return [];
    
    return [
      { name: 'Revenue', value: plData.totalRevenue || 0, fill: COLORS.success },
      { name: 'Expenses', value: plData.totalExpenses || 0, fill: COLORS.danger },
      { name: 'Profit', value: Math.max(0, plData.grossProfit || 0), fill: COLORS.primary },
    ];
  }, [plData]);

  // Area distribution data
  const areaDistributionData = useMemo(() => {
    if (!plData) return [];
    
    return [
      { name: 'Existing Area', value: plData.existingCarpetArea || 0, color: COLORS.slate },
      { name: plData?.isGreenfield ? 'Landowner Area' : 'Free Area (Society)', value: plData.totalFreeArea || 0, color: COLORS.purple },
      { name: 'Sale Area (Developer)', value: plData.balanceCarpetArea || 0, color: COLORS.success },
    ].filter(item => item.value > 0);
  }, [plData]);

  // FSI Components data
  const fsiComponentsData = useMemo(() => {
    if (!fsiBreakdown) return [];
    
    const data = [
      { name: 'Base FSI', value: fsiBreakdown.baseFSIArea || 0, color: COLORS.primary },
    ];
    
    if (fsiBreakdown.incentiveFSIArea > 0) {
      data.push({ name: 'Incentive FSI', value: fsiBreakdown.incentiveFSIArea, color: COLORS.success });
    }
    if (fsiBreakdown.paidFSIArea > 0) {
      data.push({ name: 'Paid FSI', value: fsiBreakdown.paidFSIArea, color: COLORS.warning });
    }
    if (fsiBreakdown.tdrArea > 0) {
      data.push({ name: 'TDR', value: fsiBreakdown.tdrArea, color: COLORS.purple });
    }
    if (fsiBreakdown.greenFSIArea > 0) {
      data.push({ name: 'Green FSI', value: fsiBreakdown.greenFSIArea, color: COLORS.emerald });
    }
    if (fsiBreakdown.totalAncillary > 0) {
      data.push({ name: 'Ancillary', value: fsiBreakdown.totalAncillary, color: COLORS.cyan });
    }
    
    return data;
  }, [fsiBreakdown]);

  // ROI Gauge data
  const roiGaugeData = useMemo(() => {
    if (!metrics) return [];
    const roi = Math.min(Math.max(metrics.roi, 0), 100); // Clamp between 0-100
    return [
      { name: 'ROI', value: roi, fill: metrics.isProfitable ? COLORS.success : COLORS.danger }
    ];
  }, [metrics]);

  // Revenue breakdown by property type
  const revenueByTypeData = useMemo(() => {
    if (!plData || !formData) return [];
    const conversionFactor = 0.092903;
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const totalExisting = existingFlats + existingOffices + existingShops;
    
    const totalRevenue = plData.totalRevenue || 0;
    if (totalExisting <= 0 || totalRevenue <= 0) return [];
    
    const data = [];
    if (existingFlats > 0) {
      const rateFlats = parseFloat(formData.saleRateFlats) || 0;
      const proportion = existingFlats / totalExisting;
      data.push({ name: 'Flats', value: totalRevenue * proportion, color: COLORS.primary });
    }
    if (existingOffices > 0) {
      const rateOffices = parseFloat(formData.saleRateOffices) || 0;
      const proportion = existingOffices / totalExisting;
      data.push({ name: 'Offices', value: totalRevenue * proportion, color: COLORS.warning });
    }
    if (existingShops > 0) {
      const rateShops = parseFloat(formData.saleRateShops) || 0;
      const proportion = existingShops / totalExisting;
      data.push({ name: 'Shops', value: totalRevenue * proportion, color: COLORS.emerald });
    }
    return data;
  }, [plData, formData]);

  if (!plData || !fsiBreakdown) {
    return (
      <div className="space-y-6">
        <Card className="rounded-sm">
          <CardContent className="py-12 text-center">
            <PieChartIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Enter project details in Quick Input to view dashboard analytics
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      {/* Basic-FSI savings what-if banner (informational only) */}
      {plData?.fsiTotalCostsBasicMode != null && plData.fsiTotalCosts > plData.fsiTotalCostsBasicMode && (
        <div
          className="flex items-center gap-3 p-3 rounded-sm border border-blue-200 bg-blue-50/60"
          data-testid="basic-fsi-savings-banner"
        >
          <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <TrendingDown className="h-4 w-4 text-blue-700" />
          </div>
          <div className="text-sm">
            <span className="font-semibold text-blue-800">
              You could save ₹{formatCrores(plData.fsiTotalCosts - plData.fsiTotalCostsBasicMode)} Cr
            </span>
            <span className="text-muted-foreground"> by dropping Paid FSI + TDR (Basic FSI 1.1 only scenario). </span>
            <span className="text-[11px] text-blue-700/80">
              Full FSI cost: ₹{formatCrores(plData.fsiTotalCosts)} Cr · Basic-only: ₹{formatCrores(plData.fsiTotalCostsBasicMode)} Cr
            </span>
          </div>
        </div>
      )}
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-sm" data-testid="metric-revenue">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold text-green-600">₹{formatCrores(metrics?.totalRevenue)} Cr</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-sm" data-testid="metric-expenses">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Expenses</p>
                <p className="text-xl font-bold text-red-600">₹{formatCrores(metrics?.totalExpenses)} Cr</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-sm" data-testid="metric-profit">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Net Profit</p>
                <p className={`text-xl font-bold ${metrics?.isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                  ₹{formatCrores(metrics?.netProfit)} Cr
                </p>
              </div>
              <div className={`h-10 w-10 rounded-full ${metrics?.isProfitable ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center`}>
                <IndianRupee className={`h-5 w-5 ${metrics?.isProfitable ? 'text-green-600' : 'text-red-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-sm" data-testid="metric-roi">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">ROI</p>
                <p className={`text-xl font-bold ${metrics?.roi >= 15 ? 'text-green-600' : metrics?.roi >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {metrics?.roi?.toFixed(1)}%
                </p>
              </div>
              <div className={`h-10 w-10 rounded-full ${metrics?.roi >= 15 ? 'bg-green-100' : metrics?.roi >= 0 ? 'bg-yellow-100' : 'bg-red-100'} flex items-center justify-center`}>
                <LayoutGrid className={`h-5 w-5 ${metrics?.roi >= 15 ? 'text-green-600' : metrics?.roi >= 0 ? 'text-yellow-600' : 'text-red-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Cost Breakdown Pie Chart */}
        <Card className="rounded-sm" data-testid="chart-cost-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="h-4 w-4" />
              Cost Breakdown
            </CardTitle>
            <CardDescription>Distribution of total project expenses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {costBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {costBreakdownData.map((entry) => (
                <div key={`cost-legend-${entry.name}`} className="flex items-center gap-1 text-xs">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                  <span>{entry.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Revenue vs Expenses Bar Chart */}
        <Card className="rounded-sm" data-testid="chart-revenue-expense">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Revenue vs Expenses
            </CardTitle>
            <CardDescription>Financial summary comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueExpenseData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tickFormatter={(v) => `₹${formatCrores(v)} Cr`} />
                  <YAxis type="category" dataKey="name" width={70} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Area Distribution */}
        <Card className="rounded-sm" data-testid="chart-area-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Area Distribution
            </CardTitle>
            <CardDescription>Carpet area allocation (sq mtr)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={areaDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value.toFixed(0)}`}
                  >
                    {areaDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value.toFixed(2)} sq mtr`, '']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <div className="p-2 bg-slate-100 rounded-sm">
                <p className="text-xs text-muted-foreground">{plData?.isGreenfield ? 'Landowner Share' : 'Society Share'}</p>
                <p className="font-semibold text-sm">{plData?.societySharePercent?.toFixed(1) || 0}%</p>
              </div>
              <div className="p-2 bg-green-100 rounded-sm">
                <p className="text-xs text-muted-foreground">Developer Share</p>
                <p className="font-semibold text-sm">{plData?.developerSharePercent?.toFixed(1) || 0}%</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-sm">
                <p className="text-xs text-muted-foreground">Total Carpet</p>
                <p className="font-semibold text-sm">{plData?.totalCarpetArea?.toFixed(0) || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FSI Components */}
        <Card className="rounded-sm" data-testid="chart-fsi-components">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <LayoutGrid className="h-4 w-4" />
              FSI Components
            </CardTitle>
            <CardDescription>Buildable area breakdown (sq mtr)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fsiComponentsData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => v.toFixed(0)} />
                  <Tooltip formatter={(value) => [`${value.toFixed(2)} sq mtr`, '']} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {fsiComponentsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Total FSI */}
            <div className="mt-2 p-3 bg-primary/10 rounded-sm text-center">
              <p className="text-xs text-muted-foreground">Total FSI Area (with Ancillary)</p>
              <p className="font-bold text-lg">{fsiBreakdown?.totalFSIWithAncillary?.toFixed(2) || 0} sq mtr</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 - Revenue Breakdown & Property Details */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Revenue Breakdown by Property Type */}
        {revenueByTypeData.length > 0 && (
          <Card className="rounded-sm" data-testid="chart-revenue-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <PieChartIcon className="h-4 w-4" />
                Revenue by Property Type
              </CardTitle>
              <CardDescription>Revenue split across property types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={revenueByTypeData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {revenueByTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`₹${formatCrores(value)} Cr`, '']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                {revenueByTypeData.map((item) => (
                  <div key={`revenue-${item.name}`} className="p-2 rounded-sm" style={{ backgroundColor: `${item.color}15` }}>
                    <p className="text-xs text-muted-foreground">{item.name}</p>
                    <p className="font-semibold text-sm">₹{formatCrores(item.value)} Cr</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Property & Development Details */}
        <Card className="rounded-sm" data-testid="property-details-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Property & Development Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Existing Areas</h4>
                <div className="space-y-1 text-sm">
                  {formData?.hasFlats && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Flats</span>
                      <span className="font-mono">{parseInt(formData?.areaFlats || 0).toLocaleString('en-IN')} sft</span>
                    </div>
                  )}
                  {formData?.hasOffices && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Offices</span>
                      <span className="font-mono">{parseInt(formData?.areaOffices || 0).toLocaleString('en-IN')} sft</span>
                    </div>
                  )}
                  {formData?.hasShops && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shops</span>
                      <span className="font-mono">{parseInt(formData?.areaShops || 0).toLocaleString('en-IN')} sft</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t pt-2 space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Key Metrics</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total FSI Area</span>
                    <span className="font-mono">{fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sq mtr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Carpet Area</span>
                    <span className="font-mono">{plData?.totalCarpetArea?.toFixed(0)} sq mtr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Free Area %</span>
                    <span className="font-mono">{formData?.freeAreaPercentFlats || formData?.additionalFreeAreaPercent || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Loading %</span>
                    <span className="font-mono">{formData?.loadingPercentage || 40}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Carpet Area Factor</span>
                    <span className="font-mono">{formData?.carpetAreaFactor || 70}%</span>
                  </div>
                </div>
              </div>
              <div className="border-t pt-2 space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Financial Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sale Rate (Flats)</span>
                    <span className="font-mono">₹{parseInt(formData?.saleRateFlats || 0).toLocaleString('en-IN')}/sft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Construction Cost</span>
                    <span className="font-mono">₹{parseInt(formData?.constructionCostPerSqft || 0).toLocaleString('en-IN')}/sft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bank Interest</span>
                    <span className="font-mono">₹{formatCrores(plData?.totalBankInterest)} Cr</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Insights Section */}
      <Card className="rounded-sm border-amber-200 bg-amber-50/30" data-testid="key-insights">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-600" />
            Key Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-white rounded-sm border space-y-1">
              <h4 className="font-semibold text-sm text-green-700">Profit Margin</h4>
              <p className="text-sm text-muted-foreground">
                Project has a <span className="font-semibold text-foreground">{plData?.profitMarginPercent?.toFixed(1)}%</span> profit margin.
                {plData?.profitMarginPercent > 30 ? ' Strong profitability.' : plData?.profitMarginPercent > 15 ? ' Moderate profitability.' : ' Tight margins.'}
              </p>
            </div>
            <div className="p-3 bg-white rounded-sm border space-y-1">
              <h4 className="font-semibold text-sm text-blue-700">Construction Share</h4>
              <p className="text-sm text-muted-foreground">
                Construction is <span className="font-semibold text-foreground">{metrics?.totalExpenses > 0 ? ((plData?.totalConstructionCost / metrics?.totalExpenses) * 100).toFixed(0) : 0}%</span> of total expenses.
                {((plData?.totalConstructionCost / metrics?.totalExpenses) * 100) > 70 ? ' Largest cost component.' : ''}
              </p>
            </div>
            <div className="p-3 bg-white rounded-sm border space-y-1">
              <h4 className="font-semibold text-sm text-purple-700">Revenue per Sft</h4>
              <p className="text-sm text-muted-foreground">
                Earning <span className="font-semibold text-foreground">₹{plData?.finalSaleAreaSqft > 0 ? ((plData?.grossProfit / plData?.finalSaleAreaSqft)).toFixed(0) : 0}/sft</span> profit on saleable area of {plData?.finalSaleAreaSqft?.toFixed(0)?.replace(/\B(?=(\d{2})*(\d{3})(?!\d))/g, ',')} sft.
              </p>
            </div>
            <div className="p-3 bg-white rounded-sm border space-y-1">
              <h4 className="font-semibold text-sm text-orange-700">Investment Returns</h4>
              <p className="text-sm text-muted-foreground">
                ROI of <span className="font-semibold text-foreground">{metrics?.roi?.toFixed(1)}%</span> on ₹{formatCrores(plData?.initialInvestment)} Cr initial investment.
                {metrics?.roi > 50 ? ' Excellent returns.' : metrics?.roi > 25 ? ' Good returns.' : ' Returns need improvement.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profitability Waterfall & Cost per Sqft Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Profitability Waterfall */}
        <Card className="rounded-sm" data-testid="chart-profitability-waterfall">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Profit Waterfall
            </CardTitle>
            <CardDescription>Revenue to profit breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Revenue', value: metrics?.totalRevenue || 0, fill: COLORS.success },
                  { name: 'FSI Costs', value: -(plData?.fsiTotalCosts || 0), fill: COLORS.danger },
                  { name: 'DA Charges', value: -(plData?.totalDACharges || 0), fill: COLORS.warning },
                  { name: plData?.isGreenfield ? 'JV Costs' : 'Society', value: -(plData?.totalSocietyExpenses || 0), fill: COLORS.purple },
                  { name: 'Construction', value: -(plData?.totalConstructionCost || 0), fill: COLORS.secondary },
                  { name: 'Other', value: -(plData?.totalOtherCosts || 0), fill: COLORS.cyan },
                  { name: 'Interest', value: -(plData?.totalBankInterest || 0), fill: '#f97316' },
                  { name: 'Profit', value: plData?.grossProfit || 0, fill: COLORS.primary },
                ]} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => `${(Math.abs(v) / 10000000).toFixed(0)} Cr`} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(value) => [`₹${formatCrores(Math.abs(value))} Cr`, value >= 0 ? 'Add' : 'Deduct']} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {[
                      { fill: COLORS.success },
                      { fill: COLORS.danger },
                      { fill: COLORS.warning },
                      { fill: COLORS.purple },
                      { fill: COLORS.secondary },
                      { fill: COLORS.cyan },
                      { fill: '#f97316' },
                      { fill: COLORS.primary },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Cost Per Sqft Analysis */}
        <Card className="rounded-sm" data-testid="chart-cost-per-sqft">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <IndianRupee className="h-4 w-4" />
              Per Sqft Analysis
            </CardTitle>
            <CardDescription>Rate breakdown per saleable sqft</CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const saleArea = plData?.finalSaleAreaSqft || 1;
              const perSqftData = [
                { name: 'Sale Rate', value: (metrics?.totalRevenue || 0) / saleArea, color: COLORS.success },
                { name: 'Construction', value: (plData?.totalConstructionCost || 0) / saleArea, color: COLORS.secondary },
                { name: plData?.isGreenfield ? 'JV Costs' : 'Society', value: ((plData?.totalSocietyExpenses || 0) + (plData?.pmcCost || 0)) / saleArea, color: COLORS.purple },
                { name: 'FSI + DA', value: ((plData?.fsiTotalCosts || 0) + (plData?.totalDACharges || 0)) / saleArea, color: COLORS.warning },
                { name: 'Other', value: ((plData?.totalOtherCosts || 0) + (plData?.totalBankInterest || 0)) / saleArea, color: COLORS.cyan },
                { name: 'Profit', value: (plData?.grossProfit || 0) / saleArea, color: COLORS.primary },
              ];
              return (
                <div className="space-y-3">
                  {perSqftData.map((item) => (
                    <div key={`persqft-${item.name}`} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{item.name}</span>
                        <span className="font-mono font-semibold">₹{item.value.toFixed(0).replace(/\B(?=(\d{2})*(\d{3})(?!\d))/g, ',')}/sft</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, (item.value / perSqftData[0].value) * 100)}%`,
                            backgroundColor: item.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <Separator className="my-2" />
                  <div className="flex justify-between text-sm font-semibold">
                    <span>Total Cost/sft</span>
                    <span className="font-mono">₹{((metrics?.totalExpenses || 0) / (plData?.finalSaleAreaSqft || 1)).toFixed(0).replace(/\B(?=(\d{2})*(\d{3})(?!\d))/g, ',')}/sft</span>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* ROI Gauge and Project Summary */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* ROI Gauge */}
        <Card className="rounded-sm" data-testid="chart-roi-gauge">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Project Viability</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart 
                  cx="50%" 
                  cy="50%" 
                  innerRadius="60%" 
                  outerRadius="100%" 
                  barSize={15}
                  data={roiGaugeData}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    minAngle={15}
                    background
                    clockWise
                    dataKey="value"
                    cornerRadius={10}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center -mt-8">
              <p className={`text-3xl font-bold ${metrics?.isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                {metrics?.roi?.toFixed(1)}%
              </p>
              <p className="text-sm text-muted-foreground">Return on Investment</p>
              <div className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                metrics?.isProfitable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {metrics?.isProfitable ? 'VIABLE' : 'NOT VIABLE'}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* JV Comparison — Greenfield only */}
        {formData?.projectMode === "greenfield" && plData && (
          <JVComparisonCard formData={formData} fsiBreakdown={fsiBreakdown} fsiCosts={fsiCosts} daChargesCosts={daChargesCosts} societyCosts={societyCosts} plData={plData} />
        )}

        {/* Quick Stats */}
        <Card className="rounded-sm md:col-span-2" data-testid="quick-stats">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Project Quick Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Plot Area</p>
                <p className="font-semibold">{fsiBreakdown?.grossPlotArea?.toFixed(0) || 0} sq mtr</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Total Constructed Area</p>
                <p className="font-semibold">{plData?.totalConstructedAreaSqft?.toFixed(0) || 0} sft</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Sale Area</p>
                <p className="font-semibold">{plData?.finalSaleAreaSqft?.toFixed(0) || 0} sft</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Sale Rate (Avg)</p>
                <p className="font-semibold">₹{plData?.saleRatePerSqft?.toFixed(0) || 0}/sft</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Duration</p>
                <p className="font-semibold">{formData?.projectDuration || 0} months</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">{formData?.projectMode === 'greenfield' ? 'JV Type' : 'Members'}</p>
                <p className="font-semibold">{formData?.projectMode === 'greenfield' ? (formData?.jvType === 'revenue_sharing' ? 'Revenue Share' : 'Area Share') : (formData?.numMembers || 0)}</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Construction Cost</p>
                <p className="font-semibold">₹{formatCrores(plData?.totalConstructionCost)} Cr</p>
                <p className="text-xs text-muted-foreground">@ ₹{formData?.constructionCostPerSqft || 0}/sft</p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Initial Investment</p>
                <p className="font-semibold">₹{formatCrores(plData?.initialInvestment)} Cr</p>
              </div>
              <div className="p-3 rounded-sm bg-emerald-50 border border-emerald-200" data-testid="quick-stats-initial-investment-basic">
                <p className="text-xs text-emerald-700">Init. Investment — Basic FSI</p>
                {plData?.initialInvestmentBasicMode != null ? (
                  <>
                    <p className="font-semibold text-emerald-800">₹{formatCrores(plData.initialInvestmentBasicMode)} Cr</p>
                    {(plData.initialInvestment - plData.initialInvestmentBasicMode) > 0 && (
                      <p className="text-[10px] text-emerald-700">
                        Saves ₹{formatCrores(plData.initialInvestment - plData.initialInvestmentBasicMode)} Cr upfront
                      </p>
                    )}
                  </>
                ) : (
                  <p className="font-semibold text-muted-foreground">—</p>
                )}
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm" data-testid="quick-stats-sharing-ratio">
                <p className="text-xs text-muted-foreground">
                  {plData?.isGreenfield ? 'JV Ratio' : 'Sharing Ratio'}
                </p>
                <p className="font-semibold">
                  {plData?.societySharePercent > 0
                    ? `${plData.societySharePercent.toFixed(0)}:${plData.developerSharePercent.toFixed(0)}`
                    : '—'}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {plData?.isGreenfield 
                    ? `Landowner : Developer (${plData?.jvType === 'revenue_sharing' ? 'Revenue' : 'Area'})` 
                    : 'Society : Builder'}
                </p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm" data-testid="quick-stats-total-cost-sft">
                <p className="text-xs text-muted-foreground">Total Cost / Sft</p>
                <p className="font-semibold">
                  ₹{plData?.finalSaleAreaSqft > 0 ? Math.round((plData?.totalExpenses || 0) / plData.finalSaleAreaSqft).toLocaleString('en-IN') : '—'}/sft
                </p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Profit Margin</p>
                <p className="font-semibold">{plData?.profitMarginPercent?.toFixed(1) || 0}%</p>
              </div>
              <div className={`p-3 rounded-sm ${(plData?.grossProfit || 0) >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs text-muted-foreground">Net Profit</p>
                <p className={`font-semibold ${(plData?.grossProfit || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="quick-stats-net-profit">₹{formatCrores(plData?.grossProfit)} Cr</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
